import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBox,
  FaChevronLeft,
  FaChevronRight,
  FaFire,
  FaGlobeAmericas,
  FaIndustry,
  FaSearch,
  FaTimes,
  FaTruck,
  FaUsers,
} from "react-icons/fa";
import { LuGavel, LuWrench } from "react-icons/lu";
import HubHomeFooter from "../hub/HubHomeFooter";
import MarketplaceAdSection from "./MarketplaceAdSection";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { MASTER_CATEGORY_IMAGE_BY_SLUG, masterCategoryToSlug } from "../../constants/masterCategoryImageMap";
import { goToBuyerRfqPost, goToQuickRfqCreation, goToSupplierRegistration } from "../../utils/buyerRfqRedirect";
import { useShowListAsSupplierButton } from "../../hooks/useShowListAsSupplierButton";
import "../../assets/styles/explore_product_categories.scss";
import "../../assets/styles/product_marketplace.scss";

const QUICK_FILTERS = [
  { key: "products", label: "Products", href: "/marketplace/products" },
  { key: "cm", label: "Contract Manufacturing", href: "/marketplace/products?module=cm" },
  { key: "infra", label: "Infrastructure Leasing", href: "/marketplace/products?module=infra" },
  { key: "auctions", label: "Auctions", href: "/marketplace/products?module=auctions" },
  { key: "procurement", label: "Procurement", href: "/procurement-services" },
  { key: "services", label: "Services", href: "/logistics-providers" },
  { key: "logistics", label: "Logistics", href: "/logistics-providers" },
];

const CATEGORY_SORT_OPTIONS = [
  { value: "most_active", label: "Most Active" },
  { value: "newest", label: "Newest" },
  { value: "most_suppliers", label: "Most Suppliers" },
  { value: "most_buyer_req", label: "Most Buyer Demand" },
  { value: "auction_available", label: "Auction Available" },
];

const SUBCATEGORY_SORT_OPTIONS = [
  { value: "most_buyer_req", label: "Most Demand" },
  { value: "most_suppliers", label: "Most Suppliers" },
  { value: "newest", label: "Recently Added" },
  { value: "most_active", label: "Most Active" },
];

const CM_INDUSTRY_SORT_OPTIONS = [
  { value: "most_manufacturers", label: "Most Manufacturers" },
  { value: "most_products", label: "Most Products" },
  { value: "most_rfqs", label: "Most RFQs" },
  { value: "most_verified", label: "Most Verified" },
  { value: "name", label: "Industry A-Z" },
  { value: "relevance", label: "Relevance" },
];

const CATEGORIES_PAGE_SIZE = 9;

function formatStatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function categoryActivityScore(item) {
  return (
    (Number(item.product_count) || 0) +
    (Number(item.supplier_count) || 0) +
    (Number(item.buyer_request_count) || 0)
  );
}

function cmIndustryActivityScore(item) {
  return (
    (Number(item.supplier_count) || 0) * 3 +
    (Number(item.product_count) || 0) * 2 +
    (Number(item.rfq_count) || 0) * 2 +
    (Number(item.verified_count) || Number(item.buyer_request_count) || 0)
  );
}

function categoryDescription(name) {
  return `Discover verified ${name} suppliers, buyer requirements, and product listings tailored for B2B procurement.`;
}

function subcategoryDescription(name, parentName) {
  const parent = parentName ? `${parentName} ` : "";
  return `Explore ${name} within ${parent}—connect with verified suppliers, active buyer requirements, RFQs, and live auctions.`;
}

function CategoryCardIcon({ name }) {
  const key = String(name || "").toLowerCase();
  if (key.includes("industrial") || key.includes("hardware") || key.includes("tool")) {
    return <LuWrench aria-hidden />;
  }
  if (key.includes("textile") || key.includes("fabric")) return <FaIndustry aria-hidden />;
  if (key.includes("logistic") || key.includes("transport")) return <FaTruck aria-hidden />;
  if (key.includes("auction")) return <LuGavel aria-hidden />;
  return <FaBox aria-hidden />;
}

function CategoryHeroStats({ parentCategory }) {
  const suppliers = parentCategory?.supplier_count ?? 0;
  const buyerReqs = parentCategory?.buyer_request_count ?? 0;
  const cmOptions = Math.max(Math.round(suppliers / 19), buyerReqs > 0 ? 12 : 8);
  const countries = 42;

  const tiles = [
    { label: "Verified Suppliers", value: suppliers, icon: FaUsers },
    { label: "Active Buyer Req.", value: buyerReqs, icon: FaBox },
    { label: "Contract Mfg Options", value: cmOptions, icon: FaIndustry },
    { label: "Countries Covered", value: countries, icon: FaGlobeAmericas },
  ];

  return (
    <div className="explore-subcat-hero__stats">
      {tiles.map((tile) => (
        <div key={tile.label} className="explore-subcat-hero__stat-tile">
          <tile.icon className="explore-subcat-hero__stat-icon" aria-hidden />
          <p className="explore-subcat-hero__stat-value">{formatStatCount(tile.value)}</p>
          <p className="explore-subcat-hero__stat-label">{tile.label}</p>
        </div>
      ))}
    </div>
  );
}

function CategoryLiveInsights({ parentCategory, items = [] }) {
  const buyerReqs = parentCategory?.buyer_request_count ?? 0;
  const suppliers = parentCategory?.supplier_count ?? 0;
  const topNames = [...items]
    .sort((a, b) => (b.buyer_request_count ?? 0) - (a.buyer_request_count ?? 0))
    .slice(0, 2)
    .map((item) => item.name)
    .filter(Boolean);

  const insights = [];
  if (buyerReqs > 0) insights.push("High buyer demand this week");
  if (suppliers > 0) insights.push(`${formatStatCount(suppliers)} verified suppliers active`);
  if (topNames.length) insights.push(`Top subcategories: ${topNames.join(", ")}`);
  if (!insights.length) insights.push("Explore subcategories to find suppliers and buyer requirements");

  return (
    <aside className="explore-subcat-hero__insights" aria-label="Live insights">
      <h2>Live Insights</h2>
      <ul>
        {insights.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </aside>
  );
}

function ExploreCategoryCard({
  item,
  trending,
  exportBadge,
  onSelect,
  showStats = true,
  variant = "category",
  parentName = "",
  ctaLabel,
  descriptionBuilder,
  infraBrowseDepth = -1,
  infraListingType = "",
  auctionBrowseDepth = -1,
}) {
  const slug = item.slug || masterCategoryToSlug(item.name);
  const rawImagePath =
    item.imagePath ||
    item.image_path ||
    (variant === "category" ? MASTER_CATEGORY_IMAGE_BY_SLUG[slug] : null);
  const imageSrc = rawImagePath ? resolveHubImage(rawImagePath) : null;
  const hasCerts = (item.supplier_count ?? item.count ?? 0) > 0;
  const isSubcategory = variant === "subcategory";
  const isCmIndustry = variant === "cm-industry";
  const isInfra = variant === "infra";
  const isAuction = variant === "auction";
  const showInfraChildStat =
    !isInfra ||
    infraBrowseDepth === 0 ||
    infraBrowseDepth === 1 ||
    (infraBrowseDepth === 2 && infraListingType === "machinery");
  const infraStatLabels =
    infraBrowseDepth === 0
      ? { secondary: "Categories", tertiary: "Locations" }
      : infraBrowseDepth === 1
        ? { secondary: "Subcategories", tertiary: "Locations" }
        : infraBrowseDepth === 2
          ? { secondary: "Sub-types", tertiary: "Locations" }
          : { secondary: "Types", tertiary: "Locations" };

  return (
    <article
      className={`explore-cat-card${
        isSubcategory ? " explore-cat-card--subcategory" : ""
      }${isCmIndustry ? " explore-cat-card--cm-industry" : ""}${
        isInfra ? " explore-cat-card--infra" : ""
      }${isAuction ? " explore-cat-card--auction" : ""}`}
    >
      <button type="button" className="explore-cat-card__media-btn" onClick={() => onSelect(item.id)}>
        <div className={`explore-cat-card__media${imageSrc ? " explore-cat-card__media--has-image" : ""}`}>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={item.name}
              className="explore-cat-card__img"
              loading="lazy"
              decoding="async"
              width={640}
              height={360}
            />
          ) : (
            <div className="explore-cat-card__media-fallback">
              <CategoryCardIcon name={item.name} />
            </div>
          )}
          <div className="explore-cat-card__media-caption">
            <div className="explore-cat-card__media-icon">
              <CategoryCardIcon name={item.name} />
            </div>
            <div className="explore-cat-card__media-title-wrap">
              <div className="explore-cat-card__media-title-row">
                <h3>{item.name}</h3>
                {trending ? (
                  <span className="explore-cat-card__trending">
                    <FaFire aria-hidden /> TRENDING
                  </span>
                ) : null}
                {exportBadge ? <span className="explore-cat-card__export">EXPORT</span> : null}
              </div>
            </div>
          </div>
        </div>
      </button>

      <div className="explore-cat-card__desc-wrap">
        <p className="explore-cat-card__desc">
          {item.description ||
            (descriptionBuilder
              ? descriptionBuilder(item)
              : isSubcategory
                ? subcategoryDescription(item.name, parentName)
                : categoryDescription(item.name))}
        </p>
      </div>

      {showStats ? (
        <div
          className={`explore-cat-card__stats${
            isSubcategory || isCmIndustry
              ? " explore-cat-card__stats--4"
              : ""
          }`}
        >
          <div className="explore-cat-card__stat explore-cat-card__stat--suppliers">
            <p className="explore-cat-card__stat-value">
              {formatStatCount(
                isInfra ? item.product_count : isAuction ? item.product_count : item.supplier_count ?? item.count,
              )}
            </p>
            <p className="explore-cat-card__stat-label">
              {isInfra ? "Listings" : isAuction ? "Live Auctions" : isCmIndustry ? "Manufacturers" : "Suppliers"}
            </p>
          </div>
          {showInfraChildStat ? (
          <div className="explore-cat-card__stat explore-cat-card__stat--products">
            <p className="explore-cat-card__stat-value">
              {formatStatCount(isInfra ? item.supplier_count : isAuction ? item.supplier_count : item.product_count)}
            </p>
            <p className="explore-cat-card__stat-label">
              {isInfra
                ? infraStatLabels.secondary
                : isAuction
                  ? auctionBrowseDepth === 0
                    ? "Subcategories"
                    : "Sellers"
                  : "Products"}
            </p>
          </div>
          ) : null}
          <div className="explore-cat-card__stat explore-cat-card__stat--buyer">
            <p className="explore-cat-card__stat-value">
              {formatStatCount(
                isCmIndustry
                  ? item.verified_count ?? item.buyer_request_count
                  : item.buyer_request_count,
              )}
            </p>
            <p className="explore-cat-card__stat-label">
              {isInfra
                ? infraStatLabels.tertiary
                : isAuction
                  ? auctionBrowseDepth === 0
                    ? "Sellers"
                    : "Listings"
                  : isCmIndustry
                    ? "Verified"
                    : isSubcategory
                      ? "Buyer Req."
                      : "Buyer Reqs"}
            </p>
          </div>
          {isSubcategory || isCmIndustry ? (
            <div className="explore-cat-card__stat explore-cat-card__stat--rfqs">
              <p className="explore-cat-card__stat-value">{formatStatCount(item.rfq_count)}</p>
              <p className="explore-cat-card__stat-label">RFQs</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {isSubcategory ? (
        <div className="explore-cat-card__footer explore-cat-card__footer--subcategory">
          <button type="button" className="explore-cat-card__cta-btn" onClick={() => onSelect(item.id)}>
            Explore Further <FaChevronRight aria-hidden />
          </button>
        </div>
      ) : (
        <div className="explore-cat-card__footer">
          <div className="explore-cat-card__certs" aria-label="Certifications">
            {hasCerts ? (
              <>
                <span className="explore-cat-card__cert-pill">ISO 9001</span>
                <span className="explore-cat-card__cert-pill">CE</span>
              </>
            ) : (
              <span className="explore-cat-card__certs-empty">
                {isCmIndustry
                  ? "Growing industry"
                  : isInfra
                    ? "Infrastructure leasing"
                    : isAuction
                      ? "Live auctions"
                      : "Growing category"}
              </span>
            )}
          </div>
          <button type="button" className="explore-cat-card__link" onClick={() => onSelect(item.id)}>
            {ctaLabel || "View Subcategories"} <FaChevronRight aria-hidden />
          </button>
        </div>
      )}
    </article>
  );
}

export default function ExploreProductCategoriesBrowse({
  browseLevel = 1,
  title = "Explore Business Categories",
  subtitle = "Browse categories to find products, suppliers, and buyer requirements across the B2B marketplace.",
  breadcrumbTrail = [],
  items = [],
  loading = false,
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  onSelect,
  onBack,
  backLabel = "Back",
  filtersSidebar = null,
  showStats = true,
  parentCategory = null,
  advertisementBanners = [],
  subcategoryBanners = [],
  activeModuleKey = "products",
  onModuleSelect,
  itemLabelOverride,
  cardCtaLabel,
  cardDescriptionBuilder,
  currentCrumbLabel = "Categories",
  cardVariant,
  infraBrowseDepth = -1,
  infraListingType = "",
  auctionBrowseDepth = -1,
}) {
  const navigate = useNavigate();
  const showListAsSupplier = useShowListAsSupplierButton();
  const isSubcategoryBrowse = browseLevel === 2;
  const isCmIndustryBrowse = activeModuleKey === "cm";
  const isInfraBrowse = activeModuleKey === "infra";
  const isAuctionBrowse = activeModuleKey === "auctions";
  const resolvedCardVariant =
    cardVariant ||
    (isCmIndustryBrowse ? "cm-industry" : isInfraBrowse ? "infra" : isAuctionBrowse ? "auction" : undefined);
  const sortOptions = isCmIndustryBrowse
    ? CM_INDUSTRY_SORT_OPTIONS
    : isAuctionBrowse
      ? CATEGORY_SORT_OPTIONS
      : isSubcategoryBrowse
        ? SUBCATEGORY_SORT_OPTIONS
        : CATEGORY_SORT_OPTIONS;
  const itemLabel =
    itemLabelOverride ||
    (isCmIndustryBrowse
      ? "Industries"
      : isAuctionBrowse
        ? auctionBrowseDepth === 0
          ? "Categories"
          : "Subcategories"
        : isInfraBrowse
          ? "Categories"
          : isSubcategoryBrowse
            ? "Subcategories"
            : "Categories");
  const loadMoreItemNoun = isCmIndustryBrowse
    ? { one: "industry", many: "industries" }
    : isSubcategoryBrowse
      ? { one: "subcategory", many: "subcategories" }
      : { one: "category", many: "categories" };

  const [sort, setSort] = useState(
    isCmIndustryBrowse ? "most_manufacturers" : isSubcategoryBrowse ? "most_buyer_req" : "most_active",
  );
  const [visibleCount, setVisibleCount] = useState(CATEGORIES_PAGE_SIZE);
  const [localSearch, setLocalSearch] = useState("");

  useEffect(() => {
    if (isCmIndustryBrowse) {
      setSort("most_manufacturers");
    } else if (isSubcategoryBrowse) {
      setSort("most_buyer_req");
    } else {
      setSort("most_active");
    }
    setLocalSearch("");
  }, [browseLevel, parentCategory?.id, isSubcategoryBrowse, isCmIndustryBrowse, isAuctionBrowse, auctionBrowseDepth]);

  const effectiveSearch = isSubcategoryBrowse ? localSearch : searchValue;

  const trendingIds = useMemo(() => {
    const scoreFn = isCmIndustryBrowse
      ? cmIndustryActivityScore
      : isAuctionBrowse
        ? (item) => Number(item.product_count) || 0
        : isInfraBrowse
          ? (item) => Number(item.product_count) || 0
          : categoryActivityScore;
    const ranked = items
      .map((item) => ({ id: item.id, score: scoreFn(item) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);

    if (!ranked.length) return new Set();

    const topCount = Math.max(1, Math.ceil(ranked.length * 0.25));
    return new Set(ranked.slice(0, topCount).map((entry) => entry.id));
  }, [items, isCmIndustryBrowse, isInfraBrowse, isAuctionBrowse]);

  const exportIds = useMemo(() => {
    return new Set(items.filter((item) => (Number(item.id) || 0) % 4 === 0).map((item) => item.id));
  }, [items]);

  const filteredItems = useMemo(() => {
    let list = [...items];
    const needle = String(effectiveSearch || "").trim().toLowerCase();

    if (needle) {
      list = list.filter((item) => String(item.name || "").toLowerCase().includes(needle));
    }

    list.sort((a, b) => {
      if (isCmIndustryBrowse) {
        switch (sort) {
          case "most_products":
            return (b.product_count ?? 0) - (a.product_count ?? 0);
          case "most_rfqs":
            return (b.rfq_count ?? 0) - (a.rfq_count ?? 0);
          case "most_verified":
            return (
              (b.verified_count ?? b.buyer_request_count ?? 0) -
              (a.verified_count ?? a.buyer_request_count ?? 0)
            );
          case "name":
            return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            });
          case "relevance":
            return cmIndustryActivityScore(b) - cmIndustryActivityScore(a);
          case "most_manufacturers":
          default:
            return (b.supplier_count ?? b.count ?? 0) - (a.supplier_count ?? a.count ?? 0);
        }
      }

      if (isAuctionBrowse) {
        switch (sort) {
          case "auction_available":
          case "most_active":
          default:
            return (b.auction_count ?? b.product_count ?? 0) - (a.auction_count ?? a.product_count ?? 0);
          case "most_suppliers":
            return (b.supplier_count ?? 0) - (a.supplier_count ?? 0);
          case "newest":
            return (Number(b.id) || 0) - (Number(a.id) || 0);
        }
      }

      switch (sort) {
        case "newest":
          return (Number(b.id) || 0) - (Number(a.id) || 0);
        case "most_suppliers":
          return (b.supplier_count ?? 0) - (a.supplier_count ?? 0);
        case "most_buyer_req":
          return (b.buyer_request_count ?? 0) - (a.buyer_request_count ?? 0);
        case "auction_available":
          return (b.auction_count ?? 0) - (a.auction_count ?? 0);
        default:
          return (
            (b.product_count ?? 0) +
            (b.supplier_count ?? 0) +
            (b.buyer_request_count ?? 0) -
            ((a.product_count ?? 0) + (a.supplier_count ?? 0) + (a.buyer_request_count ?? 0))
          );
      }
    });

    return list;
  }, [items, effectiveSearch, sort, isCmIndustryBrowse, isAuctionBrowse]);

  useEffect(() => {
    setVisibleCount(CATEGORIES_PAGE_SIZE);
  }, [items, effectiveSearch, sort, browseLevel]);

  const visibleItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount],
  );

  const hasMoreCategories = visibleCount < filteredItems.length;
  const nextBatchSize = Math.min(CATEGORIES_PAGE_SIZE, filteredItems.length - visibleCount);

  const handleSearch = (e) => {
    e.preventDefault();
    if (isSubcategoryBrowse) return;
    onSearchSubmit?.(searchValue);
  };

  return (
    <>
      <section className={`explore-categories-hero${isSubcategoryBrowse ? " explore-categories-hero--subcategory" : ""}`}>
        <div className="explore-categories-hero__inner">
          <div className="explore-categories-hero__breadcrumb-row">
            <nav className="explore-categories-hero__breadcrumb" aria-label="Breadcrumb">
              <Link to="/home">Home</Link>
              <span>&gt;</span>
              {breadcrumbTrail.length === 0 ? (
                <span className="is-current">{currentCrumbLabel}</span>
              ) : (
                <>
                  <Link to="/marketplace/products">Categories</Link>
                  {breadcrumbTrail.map((part, idx) => (
                    <React.Fragment key={`${part}-${idx}`}>
                      <span>&gt;</span>
                      {idx === breadcrumbTrail.length - 1 ? (
                        <span className="is-current">{part}</span>
                      ) : (
                        <span>{part}</span>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </nav>
            {onBack ? (
              <button type="button" className="explore-categories-hero__back" onClick={onBack}>
                <FaChevronLeft aria-hidden /> {backLabel}
              </button>
            ) : null}
          </div>

          {isSubcategoryBrowse ? (
            <div className="explore-subcat-hero__body">
              <div className="explore-subcat-hero__main">
                <div className="explore-categories-hero__head">
                  <div>
                    <h1>{title}</h1>
                    <p>{subtitle}</p>
                  </div>
                </div>
                <CategoryHeroStats parentCategory={parentCategory} />
              </div>
              <CategoryLiveInsights parentCategory={parentCategory} items={items} />
            </div>
          ) : (
            <>
              <div className="explore-categories-hero__head">
                <div>
                  <h1>{title}</h1>
                  <p>{subtitle}</p>
                </div>
              </div>

              <form className="explore-categories-hero__search" onSubmit={handleSearch}>
                <FaSearch className="explore-categories-hero__search-icon" aria-hidden />
                <input
                  type="search"
                  value={searchValue}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  placeholder="Search products, suppliers, services, or categories…"
                  aria-label="Search categories"
                />
                {searchValue ? (
                  <button
                    type="button"
                    className="explore-categories-hero__search-clear"
                    aria-label="Clear search"
                    onClick={() => onSearchChange?.("")}
                  >
                    <FaTimes aria-hidden />
                  </button>
                ) : null}
                <button type="submit" className="explore-categories-hero__search-btn">
                  Search
                </button>
              </form>

              <div className="explore-categories-hero__pills" role="navigation" aria-label="Marketplace modules">
                {QUICK_FILTERS.map((pill) => {
                  const isActive = activeModuleKey === pill.key;
                  const canSelectInPage =
                    (pill.key === "cm" || pill.key === "infra" || pill.key === "auctions") && onModuleSelect;
                  if (isActive) {
                    return (
                    <span key={pill.key} className="explore-categories-hero__pill is-active">
                      {pill.label}
                    </span>
                    );
                  }
                  if (canSelectInPage) {
                    return (
                      <button
                        key={pill.key}
                        type="button"
                        className="explore-categories-hero__pill"
                        onClick={() => onModuleSelect(pill.key)}
                      >
                        {pill.label}
                      </button>
                    );
                  }
                  return (
                    <Link key={pill.key} to={pill.href} className="explore-categories-hero__pill">
                      {pill.label}
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {isSubcategoryBrowse ? (
        <section className="explore-subcat-action-bar">
          <div className="explore-subcat-action-bar__inner">
            <form
              className="explore-subcat-action-bar__search"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <FaSearch aria-hidden />
              <input
                type="search"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={`Search within ${title}…`}
                aria-label={`Search within ${title}`}
              />
              {localSearch ? (
                <button
                  type="button"
                  className="explore-subcat-action-bar__clear"
                  aria-label="Clear search"
                  onClick={() => setLocalSearch("")}
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </form>
            <div className="explore-subcat-action-bar__actions">
              <button
                type="button"
                className="explore-subcat-action-bar__btn explore-subcat-action-bar__btn--orange"
                onClick={() => goToBuyerRfqPost(navigate)}
              >
                Post Buying Requirement
              </button>
              {showListAsSupplier ? (
                <button
                  type="button"
                  className="explore-subcat-action-bar__btn explore-subcat-action-bar__btn--navy"
                  onClick={() => goToSupplierRegistration(navigate)}
                >
                  List as Supplier
                </button>
              ) : null}
              <button
                type="button"
                className="explore-subcat-action-bar__btn explore-subcat-action-bar__btn--blue"
                onClick={() => goToQuickRfqCreation(navigate)}
              >
                Start RFQ
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <div
        className={`explore-categories-main product-marketplace-layout product-marketplace-layout--explore${
          filtersSidebar ? "" : " product-marketplace-layout--no-sidebar"
        }`}
      >
        {filtersSidebar}

        <section className="product-marketplace-content explore-categories-content">
          <div className="explore-categories-toolbar">
            <p>
              Showing <strong>{visibleItems.length}</strong> of <strong>{filteredItems.length}</strong> {itemLabel}
            </p>
            <div className="explore-categories-sort" role="group" aria-label={`Sort ${itemLabel.toLowerCase()}`}>
              <span className="explore-categories-sort__label">Sort:</span>
              <div className="explore-categories-sort__pills">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`explore-categories-sort__pill${sort === opt.value ? " is-active" : ""}`}
                    aria-pressed={sort === opt.value}
                    onClick={() => setSort(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="explore-categories-grid">
              {Array.from({ length: CATEGORIES_PAGE_SIZE }).map((_, idx) => (
                <div key={idx} className="explore-cat-card explore-cat-card--skeleton" aria-hidden />
              ))}
            </div>
          ) : filteredItems.length ? (
            <>
              <div className="explore-categories-grid">
                {visibleItems.map((item) => (
                  <ExploreCategoryCard
                    key={item.id}
                    item={item}
                    trending={trendingIds.has(item.id)}
                    exportBadge={isSubcategoryBrowse && exportIds.has(item.id)}
                    onSelect={onSelect}
                    showStats={showStats || isSubcategoryBrowse || isCmIndustryBrowse || isInfraBrowse || isAuctionBrowse}
                    variant={
                      resolvedCardVariant ||
                      (isSubcategoryBrowse ? "subcategory" : "category")
                    }
                    parentName={parentCategory?.name || title}
                    ctaLabel={cardCtaLabel}
                    descriptionBuilder={cardDescriptionBuilder}
                    infraBrowseDepth={infraBrowseDepth}
                    infraListingType={infraListingType}
                    auctionBrowseDepth={auctionBrowseDepth}
                  />
                ))}
              </div>
              {hasMoreCategories ? (
                <div className="explore-categories-load-more">
                  <button
                    type="button"
                    className="explore-categories-load-more__btn"
                    onClick={() => setVisibleCount((count) => count + CATEGORIES_PAGE_SIZE)}
                  >
                    View More
                    <span className="explore-categories-load-more__count">
                      ({nextBatchSize} more{" "}
                      {nextBatchSize === 1 ? loadMoreItemNoun.one : loadMoreItemNoun.many})
                    </span>
                    <FaChevronRight aria-hidden />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="explore-categories-empty">
              No {itemLabel.toLowerCase()} match your filters yet.
            </p>
          )}
        </section>
      </div>

      {isSubcategoryBrowse ? (
        <MarketplaceAdSection
          banners={subcategoryBanners}
          ariaLabel="Marketplace subcategory promotional banners"
          hideFallback
        />
      ) : (
        <MarketplaceAdSection banners={advertisementBanners} />
      )}

      <HubHomeFooter />
    </>
  );
}
